import { makeStyles } from 'tss-react/mui';

/**
 * Styles for the inline freeform feedback card that expands below
 * the `MessageFeedbackRow` once the overflow control is activated.
 *
 * Geometry matches the GA-Sidekick Figma source
 * (`16166:21117 / 21118 / 21119`): a 400px white card with a 1px
 * hairline border, 12px radius and padding, 16px gap between the
 * textarea group and the submit button, and `items-end` so the
 * 118px primary button hugs the right edge.
 *
 * The textarea group keeps its own 12px gap between the inline title
 * (with the 16x16 dismiss glyph) and the actual textarea. The
 * textarea itself relies on the theme-level `MuiOutlinedInput`
 * overrides for the 8px radius, hairline border and brand-blue focus
 * border, plus the 14px desktop font, and adds a brand-100 focus
 * ring shadow so the focus affordance matches Figma exactly.
 */
const useStyles = makeStyles()((theme) => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.new.spacing.medium,
    padding: theme.spacing(1.5),
    width: 400,
    maxWidth: '100%',
    backgroundColor: theme.new.color.common.white,
    border: `1px solid ${theme.new.color.line.hairline}`,
    borderRadius: theme.new.shape.borderRadius.medium,
    boxSizing: 'border-box',
  },
  textareaGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    width: '100%',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.new.spacing.xsmall,
    width: '100%',
  },
  title: {
    flex: '1 1 0',
    minWidth: 0,
    color: theme.new.color.textPrimary,
    margin: 0,
  },
  closeButton: {
    width: 16,
    height: 16,
    padding: 0,
    flexShrink: 0,
    color: theme.new.color.textPrimary,
    borderRadius: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.new.color.brand[600]}`,
      outlineOffset: '2px',
    },
  },
  textfield: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      transition: theme.transitions.create(['box-shadow', 'border-color']),
      '&.Mui-focused': {
        boxShadow: `0 0 0 2px ${theme.new.color.brand[100]}`,
      },
    },
    '& textarea': {
      ...theme.typography.small_body_regular,
    },
  },
}));

export default useStyles;
